/**
 * 新品競品比較領域運算模組
 * 包含：規格標準化單位售價、搭贈後有效進價、價格競爭力自動摘要、12項優先級候選比對與依據、GTIN條碼檢核、完成度計算
 */

export interface UnitPriceResult {
  unitPrice: number | null;
  unitName: string;
  display: string;
  isReliable: boolean;
}

/**
 * 規格標準化解析與單位售價計算
 * 支援解析：錠、顆、粒、包、片、瓶、罐、支、條、盒、ml、毫升、g、公克、kg、公斤、次、日
 */
export function calculateNormalizedUnitPrice(
  price: number | null | undefined,
  specification: string | null | undefined,
  salesUnit?: string | null,
): UnitPriceResult {
  if (price === null || price === undefined || price <= 0 || !specification) {
    return {
      unitPrice: null,
      unitName: '',
      display: '單位價格待採購確認',
      isReliable: false,
    };
  }

  const spec = specification.trim().toLowerCase();

  // 1. 顆/粒/錠/膠囊/片/包/袋/瓶/罐/支/條/貼/抽
  const countRegex =
    /(\d+(?:\.\d+)?)\s*(?:顆|粒|錠|膠囊|片|包|袋|瓶|罐|支|條|貼|抽|入|份|枚|tables?|capsules?|packs?|sachets?|bottles?|pcs)/i;
  // 2. 容量/重量: ml/毫升/l/公升/g/公克/kg/公斤
  const volWeightRegex = /(\d+(?:\.\d+)?)\s*(ml|毫升|c\.c\.|cc|l|公升|g|公克|克|kg|公斤)/i;
  // 3. 次數/日數: N次 / N日 / N天 / N週
  const usageRegex = /(\d+(?:\.\d+)?)\s*(?:次|日|天|週|days?|times)/i;

  const countMatch = spec.match(countRegex);
  if (countMatch && countMatch[1]) {
    const count = parseFloat(countMatch[1]);
    if (count > 0) {
      let unitLabel = '顆';
      if (/錠/i.test(spec)) unitLabel = '錠';
      else if (/包|袋|sachet/i.test(spec)) unitLabel = '包';
      else if (/片|貼|枚/i.test(spec)) unitLabel = '片';
      else if (/瓶|罐|bottle/i.test(spec)) unitLabel = '瓶';
      else if (/支|條/i.test(spec)) unitLabel = '支';
      else if (/膠囊|粒|顆/i.test(spec)) unitLabel = '顆';

      const unitPrice = Math.round((price / count) * 100) / 100;
      return {
        unitPrice,
        unitName: `元／${unitLabel}`,
        display: `${unitPrice} 元／${unitLabel}`,
        isReliable: true,
      };
    }
  }

  const volWeightMatch = spec.match(volWeightRegex);
  if (volWeightMatch && volWeightMatch[1] && volWeightMatch[2]) {
    let amount = parseFloat(volWeightMatch[1]);
    const unitRaw = volWeightMatch[2].toLowerCase();
    let unitLabel = 'ml';

    if (/^(l|公升)$/i.test(unitRaw)) {
      amount = amount * 1000;
      unitLabel = 'ml';
    } else if (/^(kg|公斤)$/i.test(unitRaw)) {
      amount = amount * 1000;
      unitLabel = 'g';
    } else if (/^(g|公克|克)$/i.test(unitRaw)) {
      unitLabel = 'g';
    }

    if (amount > 0) {
      const unitPrice = Math.round((price / amount) * 100) / 100;
      return {
        unitPrice,
        unitName: `元／${unitLabel}`,
        display: `${unitPrice} 元／${unitLabel}`,
        isReliable: true,
      };
    }
  }

  const usageMatch = spec.match(usageRegex);
  if (usageMatch && usageMatch[1]) {
    const daysOrTimes = parseFloat(usageMatch[1]);
    if (daysOrTimes > 0) {
      const unitLabel = /次|times/i.test(spec) ? '次使用' : '日使用';
      const unitPrice = Math.round((price / daysOrTimes) * 100) / 100;
      return {
        unitPrice,
        unitName: `元／${unitLabel}`,
        display: `${unitPrice} 元／${unitLabel}`,
        isReliable: true,
      };
    }
  }

  // 嘗試從 salesUnit 判斷
  if (salesUnit) {
    return {
      unitPrice: price,
      unitName: `元／${salesUnit}`,
      display: `${price} 元／${salesUnit}`,
      isReliable: true,
    };
  }

  return {
    unitPrice: null,
    unitName: '',
    display: '單位價格待採購確認',
    isReliable: false,
  };
}

export interface GiveawayEffectiveCostResult {
  rawPurchasePrice: number;
  giveawayTerms: string;
  effectivePurchasePrice: number | null;
  isCalculated: boolean;
  display: string;
  discountPercentage: number;
}

/**
 * 中文與阿拉伯數字轉換輔助
 */
export function parseChineseOrArabicNumber(str: string): number | null {
  if (!str) return null;
  const trimmed = str.trim();
  if (/^\d+$/.test(trimmed)) {
    return parseInt(trimmed, 10);
  }

  const map: Record<string, number> = {
    零: 0,
    一: 1,
    二: 2,
    兩: 2,
    三: 3,
    四: 4,
    五: 5,
    六: 6,
    七: 7,
    八: 8,
    九: 9,
    十: 10,
    百: 100,
  };

  if (trimmed.length === 1 && map[trimmed] !== undefined) {
    return map[trimmed];
  }
  if (trimmed === '十一') return 11;
  if (trimmed === '十二') return 12;
  if (trimmed === '十三') return 13;
  if (trimmed === '十四') return 14;
  if (trimmed === '十五') return 15;
  if (trimmed === '十六') return 16;
  if (trimmed === '十七') return 17;
  if (trimmed === '十八') return 18;
  if (trimmed === '十九') return 19;
  if (trimmed === '二十') return 20;
  if (trimmed === '二十四') return 24;
  if (trimmed === '三十') return 30;
  if (trimmed === '五十') return 50;
  if (trimmed === '一百') return 100;

  // 通用簡易兩位數中文解析 (例如 二十X, 三十X)
  if (trimmed.startsWith('二十') && trimmed.length === 3 && map[trimmed[2]] !== undefined) {
    return 20 + map[trimmed[2]];
  }

  return null;
}

/**
 * 搭贈後有效進價計算
 * 支援「買12送1」、「買10送2」、「12送1」、「買1送1」、「買24送2」、「滿10送1」、「買十送一」等
 * 若為非數量搭贈（如送展示架），顯示原始文字並標註「有效進價待確認」，不得亂算。
 */
export function calculateGiveawayEffectiveCost(
  rawPurchasePrice: number | null | undefined,
  giveawayTerms: string | null | undefined,
): GiveawayEffectiveCostResult {
  if (!rawPurchasePrice || rawPurchasePrice <= 0) {
    return {
      rawPurchasePrice: 0,
      giveawayTerms: giveawayTerms || '未提供',
      effectivePurchasePrice: null,
      isCalculated: false,
      display: '目前進價未設定',
      discountPercentage: 0,
    };
  }

  const terms = (giveawayTerms || '').trim();
  if (
    !terms ||
    terms === '無' ||
    terms === '未提供' ||
    terms === '0' ||
    terms === '無搭贈' ||
    terms === '無特殊搭贈'
  ) {
    return {
      rawPurchasePrice,
      giveawayTerms: '未提供',
      effectivePurchasePrice: rawPurchasePrice,
      isCalculated: true,
      display: `${rawPurchasePrice.toLocaleString('zh-TW')} 元`,
      discountPercentage: 0,
    };
  }

  // 正則解析：(買|滿) X (單位) (送|贈) Y (單位)
  const pattern =
    /(?:買|滿|^)\s*(\d+|[一二兩三四五六七八九十百]+)\s*(?:盒|包|瓶|罐|條|支|粒|顆|入|組|件|錠|份)?\s*(?:送|贈)\s*(\d+|[一二兩三四五六七八九十百]+)\s*(?:盒|包|瓶|罐|條|支|粒|顆|入|組|件|錠|份)?/i;
  const match = terms.match(pattern);

  if (match && match[1] && match[2]) {
    const buyQty = parseChineseOrArabicNumber(match[1]);
    const freeQty = parseChineseOrArabicNumber(match[2]);

    if (buyQty !== null && freeQty !== null && buyQty > 0 && freeQty > 0) {
      const totalQty = buyQty + freeQty;
      const effective = Math.round(((buyQty * rawPurchasePrice) / totalQty) * 100) / 100;
      const discount = Math.round((1 - effective / rawPurchasePrice) * 10000) / 100;
      return {
        rawPurchasePrice,
        giveawayTerms: terms,
        effectivePurchasePrice: effective,
        isCalculated: true,
        display: `${effective.toLocaleString('zh-TW')} 元（${terms}，等效折讓 ${discount}%）`,
        discountPercentage: discount,
      };
    }
  }

  return {
    rawPurchasePrice,
    giveawayTerms: terms,
    effectivePurchasePrice: null,
    isCalculated: false,
    display: '有效進價待確認',
    discountPercentage: 0,
  };
}

export interface MultiCompetitorSummaryInput {
  proposalName: string;
  proposalPurchasePrice?: number | null;
  proposalEffectivePrice?: number | null;
  proposalVipPrice?: number | null;
  proposalGiveawayTerms?: string;
  primaryCompetitor?: any | null;
  competitors?: any[];
}

function hasNoGiveawayTerms(value?: string | null): boolean {
  const normalized = (value || '').trim();
  return !normalized || ['無', '未提供', '0', '無搭贈', '無特殊搭贈'].includes(normalized);
}

/**
 * 多競品整體決策輔助摘要產生器
 */
export function generateMultiCompetitorSummary(input: MultiCompetitorSummaryInput): string {
  const parts: string[] = [];
  const competitors = (input.competitors || []).filter(Boolean);
  const primary = input.primaryCompetitor;
  const propPrice = input.proposalPurchasePrice ?? 0;
  const propEff =
    input.proposalEffectivePrice ??
    (hasNoGiveawayTerms(input.proposalGiveawayTerms) ? propPrice : null);
  const propVip = input.proposalVipPrice ?? 0;

  if (competitors.length === 0) {
    return '尚未選擇惠生既有競品進行比較。';
  }

  // 1. 進價比較
  const compsWithPrice = competitors.filter(
    (c) => (c.current_purchase_price ?? c.average_cost ?? 0) > 0,
  );
  if (compsWithPrice.length > 0 && propPrice > 0) {
    const cheaperThanCount = compsWithPrice.filter(
      (c) => (c.current_purchase_price ?? c.average_cost ?? 0) > propPrice,
    ).length;

    if (cheaperThanCount === compsWithPrice.length) {
      parts.push(`本次新品進價（$${propPrice}）低於全數 ${compsWithPrice.length} 項既有競品。`);
    } else if (cheaperThanCount > 0) {
      parts.push(
        `本次新品進價（$${propPrice}）低於 ${compsWithPrice.length} 項競品中的 ${cheaperThanCount} 項。`,
      );
    } else {
      parts.push(`本次新品進價（$${propPrice}）略高於目前 ${compsWithPrice.length} 項既有競品。`);
    }
  }

  // 2. VIP 售價平均比較
  const compsWithVip = competitors.filter((c) => (c.vip_price ?? c.regular_sale_price ?? 0) > 0);
  if (compsWithVip.length > 0 && propVip > 0) {
    const avgVip =
      compsWithVip.reduce((sum, c) => sum + (c.vip_price ?? c.regular_sale_price ?? 0), 0) /
      compsWithVip.length;
    const diffPct = Math.round(Math.abs((propVip - avgVip) / avgVip) * 1000) / 10;
    if (Math.abs(propVip - avgVip) < 1) {
      parts.push(`本次新品建議 VIP 價（$${propVip}）與競品平均價（$${Math.round(avgVip)}）相當。`);
    } else if (propVip > avgVip) {
      parts.push(
        `本次新品建議 VIP 價（$${propVip}）高於競品平均價（$${Math.round(avgVip)}）約 ${diffPct}%。`,
      );
    } else {
      parts.push(
        `本次新品建議 VIP 價（$${propVip}）低於競品平均價（$${Math.round(avgVip)}）約 ${diffPct}%。`,
      );
    }
  }

  // 3. 主要競品有效進價比較
  if (primary) {
    const primaryPrice = primary.current_purchase_price ?? primary.average_cost ?? 0;
    const primaryGiveaway = primary.current_vendor_giveaway_terms ?? '';
    const primaryEffCost = calculateGiveawayEffectiveCost(primaryPrice, primaryGiveaway);
    const primaryEff =
      primaryEffCost.effectivePurchasePrice ??
      (hasNoGiveawayTerms(primaryGiveaway) ? primaryPrice : null);

    if (propEff !== null && primaryEff !== null && propEff > 0 && primaryEff > 0) {
      const diff = Math.round(((primaryEff - propEff) / primaryEff) * 1000) / 10;
      if (diff > 0) {
        parts.push(
          `計入搭贈後，本次新品有效進價（$${propEff}）低於主要競品【${primary.product_name}】約 ${diff}%。`,
        );
      } else if (diff < 0) {
        parts.push(
          `計入搭贈後，本次新品有效進價（$${propEff}）高於主要競品【${primary.product_name}】約 ${Math.abs(diff)}%。`,
        );
      } else {
        parts.push(
          `計入搭贈後，本次新品有效進價（$${propEff}）與主要競品【${primary.product_name}】相當。`,
        );
      }
    } else if (
      (propPrice > 0 && !hasNoGiveawayTerms(input.proposalGiveawayTerms)) ||
      (primaryPrice > 0 && !hasNoGiveawayTerms(primaryGiveaway))
    ) {
      parts.push('搭贈內容無法可靠換算，有效進價待採購確認。');
    }

    // 4. 主要競品庫存影響
    const stock = Number(primary.warehouse_stock || 0);
    const onOrder = Number(primary.on_order_quantity || 0);
    const totalPending = stock + onOrder;
    if (totalPending > 0) {
      parts.push(
        `主要競品目前庫存 ${stock} ${primary.sales_unit || '件'}${onOrder > 0 ? `、已訂未到 ${onOrder} ${primary.sales_unit || '件'}` : ''}，如新品取代需評估現有 ${totalPending} ${primary.sales_unit || '件'}之庫存去化。`,
      );
    }
  } else {
    parts.push('尚未指定主要競品。');
  }

  return parts.join(' ');
}

export interface PriceCompetitivenessInput {
  proposalName: string;
  proposalPurchasePrice: number;
  proposalEffectiveCost?: number | null;
  proposalVipPrice?: number | null;
  proposalRetailPrice?: number | null;
  proposalGiveawayTerms?: string;
  proposalNormalizedUnitPrice?: number | null;
  proposalUnitName?: string;

  competitorCode: string;
  competitorName: string;
  competitorPurchasePrice: number;
  competitorEffectiveCost?: number | null;
  competitorVipPrice?: number | null;
  competitorRetailPrice?: number | null;
  competitorGiveawayTerms?: string;
  competitorNormalizedUnitPrice?: number | null;
  competitorUnitName?: string;

  webMedianPrice?: number | null;
}

/**
 * 價格競爭力自動摘要產生器
 */
export function generatePriceCompetitivenessSummary(input: PriceCompetitivenessInput): string {
  const parts: string[] = [];

  // 1. VIP 價比較
  if (input.proposalVipPrice && input.competitorVipPrice && input.competitorVipPrice > 0) {
    const vipDiff = input.proposalVipPrice - input.competitorVipPrice;
    const vipDiffPct = Math.round((Math.abs(vipDiff) / input.competitorVipPrice) * 1000) / 10;
    if (vipDiff > 0) {
      parts.push(
        `新品建議 VIP 價 ${input.proposalVipPrice} 元，較主要競品（${input.competitorCode}）${input.competitorVipPrice} 元高 ${vipDiffPct}%`,
      );
    } else if (vipDiff < 0) {
      parts.push(
        `新品建議 VIP 價 ${input.proposalVipPrice} 元，較主要競品（${input.competitorCode}）${input.competitorVipPrice} 元低 ${vipDiffPct}%`,
      );
    } else {
      parts.push(`新品建議 VIP 價與主要競品相同（${input.proposalVipPrice} 元）`);
    }
  }

  // 2. 進價與搭贈有效進價比較
  const propCost =
    input.proposalEffectiveCost ??
    (hasNoGiveawayTerms(input.proposalGiveawayTerms) ? input.proposalPurchasePrice : null);
  const compCost =
    input.competitorEffectiveCost ??
    (hasNoGiveawayTerms(input.competitorGiveawayTerms) ? input.competitorPurchasePrice : null);

  if (propCost !== null && compCost !== null && propCost > 0 && compCost > 0) {
    const costDiff = propCost - compCost;
    const costDiffPct = Math.round((Math.abs(costDiff) / compCost) * 1000) / 10;

    let costDesc = '';
    if (input.proposalGiveawayTerms && input.proposalGiveawayTerms !== '未提供') {
      costDesc = `計入新品搭贈（${input.proposalGiveawayTerms}）後，`;
    }

    if (costDiff < 0) {
      parts.push(
        `${costDesc}新品有效進貨成本（${propCost} 元）較主要競品（${compCost} 元）低 ${costDiffPct}%，具進貨成本優勢。`,
      );
    } else if (costDiff > 0) {
      parts.push(
        `${costDesc}新品有效進貨成本（${propCost} 元）較主要競品（${compCost} 元）高 ${costDiffPct}%。`,
      );
    } else {
      parts.push(`新品進貨成本與主要競品相當（${propCost} 元）。`);
    }
  } else if (
    (input.proposalPurchasePrice > 0 && !hasNoGiveawayTerms(input.proposalGiveawayTerms)) ||
    (input.competitorPurchasePrice > 0 && !hasNoGiveawayTerms(input.competitorGiveawayTerms))
  ) {
    parts.push('搭贈內容無法可靠換算，有效進價待採購確認');
  }

  // 3. 單位價格比較
  if (
    input.proposalNormalizedUnitPrice &&
    input.competitorNormalizedUnitPrice &&
    input.proposalUnitName &&
    input.competitorUnitName &&
    input.proposalUnitName === input.competitorUnitName
  ) {
    const unitDiff = input.proposalNormalizedUnitPrice - input.competitorNormalizedUnitPrice;
    const unitDiffPct =
      Math.round((Math.abs(unitDiff) / input.competitorNormalizedUnitPrice) * 1000) / 10;
    if (unitDiff < 0) {
      parts.push(
        `在單位規格上，新品為 ${input.proposalNormalizedUnitPrice} ${input.proposalUnitName}，較競品（${input.competitorNormalizedUnitPrice} ${input.competitorUnitName}）便宜 ${unitDiffPct}%。`,
      );
    } else if (unitDiff > 0) {
      parts.push(
        `在單位規格上，新品為 ${input.proposalNormalizedUnitPrice} ${input.proposalUnitName}，較競品（${input.competitorNormalizedUnitPrice} ${input.competitorUnitName}）高 ${unitDiffPct}%。`,
      );
    }
  }

  // 4. 網路價格中位數比較
  if (input.webMedianPrice && input.webMedianPrice > 0 && input.proposalRetailPrice) {
    const webDiff = input.proposalRetailPrice - input.webMedianPrice;
    const webDiffPct = Math.round((Math.abs(webDiff) / input.webMedianPrice) * 1000) / 10;
    if (webDiff > 0) {
      parts.push(
        `與公開網路市場中位價（${input.webMedianPrice} 元）相比，新品建議售價（${input.proposalRetailPrice} 元）高出 ${webDiffPct}%。`,
      );
    } else if (webDiff < 0) {
      parts.push(
        `與公開網路市場中位價（${input.webMedianPrice} 元）相比，新品建議售價（${input.proposalRetailPrice} 元）低 ${webDiffPct}%，定價具公開市場競爭力。`,
      );
    } else {
      parts.push(`新品建議售價與公開網路中位價一致（${input.webMedianPrice} 元）。`);
    }
  }

  return parts.join('；') || '目前資料尚不足以產生自動價格競爭力摘要。';
}

export interface CandidateSearchTarget {
  product_name: string;
  brand?: string | null;
  specification?: string | null;
  product_category_code?: string | null;
  product_subcategory_code?: string | null;
  barcode?: string | null;
  license_number?: string | null;
  main_ingredients?: string | null;
  target_audience?: string | null;
  suggested_vip_price?: number | null;
  product_form?: string | null;
}

export interface ProductMasterCandidate {
  id: string;
  product_code: string;
  product_name: string;
  brand?: string | null;
  specification: string;
  product_category_code?: string | null;
  product_subcategory_code?: string | null;
  barcode_list_json?: string;
  primary_barcode_candidate?: string | null;
  additional_barcode_candidate?: string | null;
  vip_price?: number | null;
  current_purchase_price?: number | null;
  regular_sale_price?: number | null;
  record_type: string;
  ingredients_json?: string;
}

export interface CandidateMatchResult {
  candidate: ProductMasterCandidate;
  score: number;
  similarityPercentage: number;
  reasons: string[];
  isSameProductSuspect: boolean;
  recommendedRole: 'PRIMARY' | 'CANDIDATE' | 'SAME_PRODUCT' | 'EXCLUDED';
}

/**
 * 12 項優先級競品推薦與相似度判定依據演算法
 */
export function scoreCompetitorCandidate(
  target: CandidateSearchTarget,
  candidate: ProductMasterCandidate,
): CandidateMatchResult {
  // 排除非商品資料
  if (candidate.record_type !== 'PRODUCT') {
    return {
      candidate,
      score: 0,
      similarityPercentage: 0,
      reasons: ['非正式商品資料（已排除）'],
      isSameProductSuspect: false,
      recommendedRole: 'EXCLUDED',
    };
  }

  let score = 0;
  const reasons: string[] = [];
  let isSameBarcode = false;

  // 1. 國際條碼相同 (權重 40)
  if (target.barcode && target.barcode.length >= 8) {
    let candidateBarcodes: string[] = [];
    try {
      if (candidate.barcode_list_json) {
        candidateBarcodes = JSON.parse(candidate.barcode_list_json);
      }
    } catch {
      // ignore
    }
    if (candidate.primary_barcode_candidate) {
      candidateBarcodes.push(candidate.primary_barcode_candidate);
    }
    if (candidate.additional_barcode_candidate) {
      candidateBarcodes.push(candidate.additional_barcode_candidate);
    }

    if (candidateBarcodes.includes(target.barcode)) {
      score += 40;
      isSameBarcode = true;
      reasons.push('國際條碼相同');
    }
  }

  // 2. 相同商品子分類 (權重 20)
  if (
    target.product_subcategory_code &&
    candidate.product_subcategory_code &&
    target.product_subcategory_code === candidate.product_subcategory_code
  ) {
    score += 20;
    reasons.push(`同屬子分類（${candidate.product_subcategory_code}）`);
  } else if (
    target.product_category_code &&
    candidate.product_category_code &&
    target.product_category_code === candidate.product_category_code
  ) {
    // 3. 相同商品分類 (權重 10)
    score += 10;
    reasons.push(`同屬大分類（${candidate.product_category_code}）`);
  }

  // 4. 商品名稱相似度 (權重 15)
  const targetName = target.product_name.toLowerCase();
  const candName = candidate.product_name.toLowerCase();
  if (targetName === candName) {
    score += 15;
    reasons.push('商品名稱完全相符');
  } else {
    // 提取關鍵詞計算重疊
    const targetKeywords = targetName.split(/[\s,，、/\\-]+/).filter((k) => k.length >= 2);
    let matchedKeywords = 0;
    for (const kw of targetKeywords) {
      if (candName.includes(kw)) matchedKeywords++;
    }
    if (targetKeywords.length > 0 && matchedKeywords > 0) {
      const matchRatio = matchedKeywords / targetKeywords.length;
      if (matchRatio >= 0.5) {
        score += Math.round(15 * matchRatio);
        reasons.push(`商品名稱關鍵字重疊（${Math.round(matchRatio * 100)}%）`);
      }
    }
  }

  // 5. 品牌相似 (權重 10)
  if (target.brand && candidate.brand) {
    if (target.brand.trim().toLowerCase() === candidate.brand.trim().toLowerCase()) {
      score += 10;
      reasons.push(`同品牌（${target.brand}）`);
    }
  }

  // 6. 規格相近 (權重 10)
  if (target.specification && candidate.specification) {
    const targetUnit = calculateNormalizedUnitPrice(100, target.specification);
    const candUnit = calculateNormalizedUnitPrice(100, candidate.specification);
    if (targetUnit.isReliable && candUnit.isReliable && targetUnit.unitName === candUnit.unitName) {
      score += 10;
      reasons.push(`規格計量單位一致（${targetUnit.unitName}）`);
    }
  }

  // 7. VIP 價相近價格帶 (差異 < 25%) (權重 5)
  if (
    target.suggested_vip_price &&
    candidate.vip_price &&
    target.suggested_vip_price > 0 &&
    candidate.vip_price > 0
  ) {
    const diff = Math.abs(target.suggested_vip_price - candidate.vip_price);
    const pct = diff / target.suggested_vip_price;
    if (pct <= 0.15) {
      score += 5;
      reasons.push(`VIP價差異極小（小於15%）`);
    } else if (pct <= 0.25) {
      score += 3;
      reasons.push(`位於相近價格帶（差異小於25%）`);
    }
  }

  const similarityPercentage = Math.min(100, Math.max(0, score));
  const isSameProductSuspect = isSameBarcode || score >= 80;

  return {
    candidate,
    score,
    similarityPercentage,
    reasons: reasons.length ? reasons : ['分類或名稱初步符合'],
    isSameProductSuspect,
    recommendedRole: isSameProductSuspect
      ? 'SAME_PRODUCT'
      : similarityPercentage >= 40
        ? 'PRIMARY'
        : 'CANDIDATE',
  };
}

/**
 * 嚴格 GTIN 條碼驗證 (8, 12, 13, 14 碼與 Check Digit)
 */
export function isValidGtin(barcode: string | null | undefined): boolean {
  if (!barcode) return false;
  const code = barcode.trim();
  if (!/^\d{8}$|^\d{12,14}$/.test(code)) return false;

  const len = code.length;
  const digits = code.split('').map(Number);
  const checkDigit = digits[len - 1];

  let sum = 0;
  let multiplier = 3;
  for (let i = len - 2; i >= 0; i--) {
    sum += digits[i] * multiplier;
    multiplier = multiplier === 3 ? 1 : 3;
  }

  const calculatedCheck = (10 - (sum % 10)) % 10;
  return calculatedCheck === checkDigit;
}

export interface ComparisonReadinessState {
  hasPrimaryCompetitor: boolean;
  hasInternalPrice: boolean;
  hasGiveawayConfirmed: boolean;
  hasWebPriceConfirmed: boolean;
  hasIngredientsCompared: boolean;
  hasBuyerConclusion: boolean;
}

/**
 * 競品比較完成度百分比與狀態檢查
 */
export function calculateComparisonReadiness(state: ComparisonReadinessState): {
  percentage: number;
  checklist: Array<{ label: string; done: boolean; pending: boolean }>;
} {
  const items = [
    { label: '已選公司主要競品', done: state.hasPrimaryCompetitor, pending: false },
    { label: '已取得公司價格', done: state.hasInternalPrice, pending: false },
    {
      label: '廠商搭贈已確認',
      done: state.hasGiveawayConfirmed,
      pending: !state.hasGiveawayConfirmed,
    },
    {
      label: '網路價格已確認',
      done: state.hasWebPriceConfirmed,
      pending: !state.hasWebPriceConfirmed,
    },
    {
      label: '成分比較完成',
      done: state.hasIngredientsCompared,
      pending: !state.hasIngredientsCompared,
    },
    {
      label: '採購結論已完成',
      done: state.hasBuyerConclusion,
      pending: !state.hasBuyerConclusion,
    },
  ];

  const doneCount = items.filter((item) => item.done).length;
  const percentage = Math.round((doneCount / items.length) * 100);

  return {
    percentage,
    checklist: items,
  };
}

export function getCategoryCodeFromMcsNo(mcsNo?: string | null): string | null {
  if (!mcsNo) return null;
  const trimmed = mcsNo.trim().toUpperCase();
  if (!trimmed) return null;
  const firstChar = trimmed[0];
  return firstChar && /[A-Z]/.test(firstChar) ? firstChar : null;
}

export function getCategoryName(categoryCode?: string | null): string | null {
  if (!categoryCode) return null;
  const code = categoryCode.trim().toUpperCase();
  const categoryMap: Record<string, string> = {
    A: '西藥成藥',
    B: '西藥指示藥',
    C: '西藥處方藥',
    D: '管制藥品',
    E: '中藥成藥',
    F: '中藥材',
    G: '中藥製劑',
    H: '特殊營養品',
    J: '醫療器材',
    K: '美妝護理',
    L: '生活用品',
    M: '健康食品',
    N: '婦嬰用品',
    P: '一般食品',
  };
  return categoryMap[code] || null;
}

export function toNullableDecimal(val: unknown): number | null {
  if (val === null || val === undefined || val === '') return null;
  const num = Number(val);
  return isNaN(num) ? null : num;
}

export function toNullableNumber(val: unknown): number | null {
  if (val === null || val === undefined || val === '') return null;
  const num = parseInt(String(val), 10);
  return isNaN(num) ? null : num;
}

export function getFirstValidGtin(candidates: Array<string | null | undefined>): string | null {
  for (const c of candidates) {
    if (!c) continue;
    const clean = String(c).trim();
    if (clean) return clean;
  }
  return null;
}

/**
 * 惠生商品主檔統一 Mapper：保證將各層資料庫／API 回傳正確轉換為 ProductMasterSearchResult
 */
export function mapProductMasterRow(row: Record<string, any>): {
  id: string;
  itemId: string;
  productName: string;
  englishName: string | null;
  barcode: string | null;
  categoryCode: string | null;
  categoryName: string | null;
  subcategoryCode: string | null;
  subcategoryName: string | null;
  spec: string | null;
  salesUnit: string | null;
  supplierCode: string | null;
  supplierName: string | null;
  vipPrice: number | null;
  salePrice: number | null;
  purchasePrice: number | null;
  averageCost: number | null;
  stock: number | null;
} {
  const id = String(row.id || '');
  const itemId = String(
    row.itemId ||
      row.item_id ||
      row.productCode ||
      row.product_code ||
      row.legacy_product_code ||
      '',
  );
  const productName = String(row.productName || row.product_name || row.cname || '');
  const englishName = row.englishName || row.english_name || row.ename || null;

  const barcode = getFirstValidGtin([
    row.barcode,
    row.primary_barcode_candidate,
    row.item_iid,
    row.item_id2,
    row.barcode_candidate,
  ]);

  const subcategoryCode =
    row.subcategoryCode ||
    row.product_subcategory_code ||
    row.imported_subcategory_code ||
    row.mcs_no ||
    null;
  const subcategoryName =
    row.subcategoryName || row.imported_subcategory_name || row.mcs_name || null;

  const derivedCatCode =
    row.categoryCode ||
    row.product_category_code ||
    row.imported_category_code ||
    getCategoryCodeFromMcsNo(subcategoryCode);

  const categoryCode = derivedCatCode || null;
  const categoryName =
    row.categoryName || row.imported_category_name || getCategoryName(categoryCode) || null;

  const spec = row.spec || row.specification || null;
  const salesUnit = row.salesUnit || row.sales_unit || row.mea || null;

  const supplierCode =
    row.supplierCode || row.supplier_code || row.legacy_vendor_code || row.cust_id || null;
  const supplierName =
    row.supplierName || row.supplier_name || row.imported_supplier_name || row.sname || null;

  const vipPrice = toNullableDecimal(row.vipPrice ?? row.vip_price);
  const salePrice = toNullableDecimal(
    row.salePrice ?? row.regularSalePrice ?? row.regular_sale_price ?? row.sa_price,
  );
  const purchasePrice = toNullableDecimal(
    row.purchasePrice ?? row.currentPurchasePrice ?? row.current_purchase_price ?? row.in_price,
  );
  const averageCost = toNullableDecimal(row.averageCost ?? row.average_cost ?? row.avg_cost);
  const stock = toNullableNumber(row.stock ?? row.warehouseStock ?? row.warehouse_stock);

  const result = {
    id,
    itemId,
    productName,
    englishName,
    barcode,
    categoryCode,
    categoryName,
    subcategoryCode,
    subcategoryName,
    spec,
    salesUnit,
    supplierCode,
    supplierName,
    vipPrice,
    salePrice,
    purchasePrice,
    averageCost,
    stock,
  };

  // Development warning
  if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
    if (result.id && !result.productName) {
      console.warn('[ProductMasterSearch] invalid row mapping: missing productName', {
        row,
        result,
      });
    }
  }

  return result;
}
